import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2, inject, signal } from '@angular/core';
import { OrganisationServiceProxy } from './shared/services/organisation-service.proxy';
import { GetOrganisationResponse } from './shared/services/model/organisation-service.model';
import { UserConfigService } from './shared/services/user-config.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { iApplyUserConfig } from '@iapply/iapply.provider';
import { HttpClient } from '@angular/common/http';

/**
 * Service to use custom themes
 * @dynamic
 */
@Injectable({
    providedIn: 'root',
})
export class CustomThemeService {
    private _document = inject(DOCUMENT);
    private readonly renderer: Renderer2; // = inject(Renderer2);
    private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
    private readonly _organisationProxyService = inject(OrganisationServiceProxy);
    private readonly _userConfigService = inject(UserConfigService);
    private readonly _location = inject(Location);
    private http = inject(HttpClient);

    private themeName: string | undefined;
    private currentTheme: string | undefined; // the theme requested via tryLoadTheme() and not yet cleared
    private currentOrganisation: string | undefined; // the organisation whose default theme has been looked up and not yet cleared
    // private organisationId: string = '';
    // private url: string = '';
    // organisationDefaultTheme: string = '';

    private config: iApplyUserConfig; // UserConfig

    private customThemeLink: any;
    public themeLoaded = signal<boolean>(false);

    /**
     * True while a theme is still being resolved (organisation lookup, theme.json, stylesheets and scripts).
     * Form screens use this to cover the page with an opaque overlay, so the base theme is never visible before the custom theme is ready.
     */
    public themePending = signal<boolean>(false);

    private pendingCount = 0;
    private pendingGeneration = 0; // incremented whenever pending work is abandoned, so late callbacks from the old work are ignored.
    private pendingTimeout: ReturnType<typeof setTimeout> | undefined;
    private static readonly PENDING_TIMEOUT_MS = 8000; // fail-safe: never keep the screen covered forever.


    // constructor(@Inject(DOCUMENT) private readonly _document) {
    constructor() {
        this.renderer = this.rendererFactory.createRenderer(null, null);

        this._userConfigService.config$
            .pipe(takeUntilDestroyed())
            .subscribe((config: iApplyUserConfig) => {
                // Store the config
                this.config = config;
                if (this.config?.localConfig?.defaultTheme || this.config?.localConfig?.organisationId) {
                    this.getTheme(this.config.localConfig.defaultTheme!, this.config.localConfig.organisationId!);
                }
            });

        // let theme = this._userConfigService.get('default-theme');
        // this._userConfigService.config = { 'defaultTheme': '' };
    }

    public init(): void {
        // document.querySelector('link[id="custom-theme"]')
        // this.customThemeLink = this._document.getElementById('custom-theme');

        // if (!this.customThemeLink) {
        //     this.customThemeLink = this.renderer.createElement('link');
        //     this.renderer.setAttribute(this.customThemeLink, 'rel', 'stylesheet');
        //     this.renderer.setAttribute(this.customThemeLink, 'id', 'custom-theme');
        //     this.renderer.appendChild(this._document.body, this.customThemeLink);
        //     // this.renderer.appendChild(this._document.head, this.customThemeLink); // _document.head is less specific than themes applied in the BODY
        // }

        // this.customThemeLink.onload = () => {
        //     console.debug('ThemeService: Theme Loaded!', this.url);
        //     // Perform actions after the stylesheet has loaded
        //     setTimeout(() => this.themeLoaded.set(true), 100);
        // };

        // this.customThemeLink.onerror = (err: any) => {
        //     console.warn('ThemeService: Error loading custom theme stylesheet:', this.url, err);
        //     // console.warn('Error loading custom theme stylesheet:', (<HTMLLinkElement>this.customThemeLink).href, err);
        // };

        // if (this.url?.trim() != '') {
        //     this.addCustomTheme(this.url);
        // }
    }

    getTheme(themeName: string, organisationId: string) {
        // this.themeName = themeName || this.config?.localConfig?.defaultTheme || undefined;
        // this.organisationId = organisationId;

        let theme = themeName || this.config?.localConfig?.defaultTheme || undefined;

        if (!theme && !organisationId) {
            // REMOVE the currently installed theme...
            this.clearTheme();
            return;
        }

        if (theme) {
            this.tryLoadTheme(theme);
        } else {
            this.getOrganisationTheme(organisationId);
        }
    }

    private tryLoadTheme(themeName: string) {
        // If same theme is already loaded
        if (this.currentTheme === themeName) {
            return;
        }

        // Theme is different here. Rather than relying on the effect to clear the theme, we can do it here in a synchronous way...
        this.clearTheme();
        this.currentTheme = themeName;

        // See if there a custom theme definition file.
        const url = this._location.prepareExternalUrl('themes/' + themeName + '/theme.json');
        const done = this.beginPending();
        this.http.get(url).subscribe({
            next: (data) => {
                // Ignore a late response for a theme that has since been cleared or replaced.
                if (this.currentTheme === themeName) {
                    // Applied directly (not via an effect) so any styles/scripts it adds are counted as pending before we call done().
                    this.applyTheme(data);
                }
                done();
            },
            error: () => done()
        });

        // always try and load the custom stylesheet:
        this.loadStyles(themeName, ['~/theme.css']);
    }

    /** Applies the contents of a theme definition file (theme.json). */
    private applyTheme(theme: any) {
        // prevent duplicate loads...
        if (!theme || theme.name === this.themeName) {
            return;
        }

        this.themeName = theme.name;
        this.setFavicon(theme.name, theme.favicon);
        this.loadStyles(theme.name, theme.styles ?? []);
        this.loadScripts(theme.name, theme.scripts ?? [], (event: any) => {
            setTimeout(() => this.themeLoaded.set(true), 100);
        });
    }

    /**
     * Marks the start of some asynchronous theme work (a request, a stylesheet or a script) and sets themePending.
     * Returns a function to call when that work has finished (successfully or not).
     */
    private beginPending(): () => void {
        const generation = this.pendingGeneration;
        let finished = false;

        this.pendingCount++;
        this.themePending.set(true);

        if (!this.pendingTimeout) {
            this.pendingTimeout = setTimeout(() => {
                console.warn('ThemeService: Timed out waiting for the theme to load.');
                this.resetPending();
            }, CustomThemeService.PENDING_TIMEOUT_MS);
        }

        return () => {
            // Ignore work that was abandoned (cleared/replaced/timed out) or already reported.
            if (finished || generation !== this.pendingGeneration) {
                return;
            }

            finished = true;
            this.pendingCount--;
            if (this.pendingCount <= 0) {
                this.resetPending();
            }
        };
    }

    /** Abandons any outstanding pending work and clears themePending. */
    private resetPending() {
        clearTimeout(this.pendingTimeout);
        this.pendingTimeout = undefined;
        this.pendingCount = 0;
        this.pendingGeneration++;
        this.themePending.set(false);
    }

    private loadScripts(themeName: string, scripts: string[], callback?: (event: any) => boolean | void) {
        scripts.forEach((url: string, index: number) => {
            if (url.startsWith('~')) {
                url = url.replace('~', `themes/${ themeName }`);
            }

            if (this.checkDuplicates('script', url)) {
                return;
            }

            let script: HTMLScriptElement = this.renderer.createElement('script');
            this.renderer.setAttribute(script, 'src', url);
            this.renderer.setAttribute(script, 'type', 'text/javascript');
            this.renderer.setAttribute(script, 'data-iapply-theme', themeName);
            // this.renderer.setAttribute(script, 'id', `custom-script-${index + 1}`);
            const done = this.beginPending();
            const unlistenError = this.renderer.listen(script, 'error', () => {
                console.warn('ThemeService: Error loading theme script:', url);
                unlistenLoad();
                unlistenError();
                done();
            });
            const unlistenLoad = this.renderer.listen(script, 'load', (event: any): boolean | void => {
                console.log('theme script loaded', themeName, url, event);
                // Optional: Callback function to execute after the script is loaded
                callback?.(event);
                unlistenLoad();
                unlistenError();
                done();
            });
            this.renderer.appendChild(this._document.body, script);
        });
    }

    private loadStyles(themeName: string, styles: string[]) {
        styles.forEach((url, index) => {
            if (url.startsWith('~')) {
                url = url.replace('~', `themes/${ themeName }`);
            }

            if (this.checkDuplicates('link', url)) {
                return;
            }

            let link: HTMLLinkElement = this.renderer.createElement('link');
            this.renderer.setAttribute(link, 'href', url);
            this.renderer.setAttribute(link, 'rel', 'stylesheet');
            this.renderer.setAttribute(link, 'type', 'text/css');
            this.renderer.setAttribute(link, 'data-iapply-theme', themeName);
            // this.renderer.setAttribute(link, 'id', `custom-theme-${index + 1}`);

            // The stylesheet is "pending" until the browser has loaded (or failed to load) it.
            const done = this.beginPending();
            const unlistenError = this.renderer.listen(link, 'error', () => {
                console.warn('ThemeService: Error loading theme stylesheet:', url);
                unlistenLoad();
                unlistenError();
                done();
            });
            const unlistenLoad = this.renderer.listen(link, 'load', () => {
                unlistenLoad();
                unlistenError();
                done();
            });

            this.renderer.appendChild(this._document.body, link);
            console.log('Theme stylesheet added: ' + url);
        });
    }

    private setFavicon(themeName: string, iconName: string) {
        // const favicon: HTMLLinkElement = this._document.querySelector("link[rel*='icon']");
        const favicon: HTMLLinkElement | null = this._document.querySelector('#appIcon');
        if (favicon) {
            const defaultHref = favicon.getAttribute('data-href') || '';
            let href = (!!themeName && !!iconName)
                ? `themes/${ themeName }/${ iconName }`
                : defaultHref;
            this.renderer.setAttribute(favicon, 'href', href);
        }
    }

        private checkDuplicates(nodeName: string, src: string): boolean {
        let el = null;
        switch (nodeName) {
            case 'script':
                el = this._document.querySelector('script[src="'+ src +'"]');
                break;
            case 'link':
                el = this._document.querySelector('link[href="'+ src +'"]');
                break;
        }

        return !!el;
    }

    // private addCustomTheme(url: string) {
    //     this.url = url;
    //     if (this.customThemeLink) {
    //         this.themeLoaded.set(false);
    //         this.renderer.removeAttribute(this.customThemeLink, 'href');
    //         this.renderer.setAttribute(this.customThemeLink, 'href', url);
    //     }
    // }

    // private removeCustomTheme() {
    //     this.themeName = '';
    //     this.url = '';
    //     this.themeLoaded.set(false);
    //     if (this.customThemeLink) {
    //         this.renderer.removeAttribute(this.customThemeLink, 'href');
    //     }
    // }

    private getOrganisationTheme(organisationId: string) {

        // This organisation has already been looked up (or is being looked up). The product re-emits while a form is being
        // edited, and repeating the lookup would needlessly show the loading overlay again.
        if (this.currentOrganisation === organisationId) {
            return;
        }
        this.currentOrganisation = organisationId;

        // The theme isn't known until the organisation has been looked up, so that lookup is pending work too.
        const generation = this.pendingGeneration;
        const done = this.beginPending();

        // TODO: The form service has a copy of the org in memory already!
        this._organisationProxyService.getOrganisation(organisationId).subscribe({
            next: (response: GetOrganisationResponse) => {
                // Ignore a late response if the theme has since been cleared or replaced.
                if (generation === this.pendingGeneration && response.success && response.organisation?.defaultTheme) {
                    let themeName = response.organisation.defaultTheme;
                    // const url = this._location.prepareExternalUrl('themes/' + themeName + '/theme.css');
                    // this.addCustomTheme(url);
                    this.tryLoadTheme(themeName); // (this clears the theme state, including currentOrganisation)
                    this.currentOrganisation = organisationId;
                }
                done();
            },
            error: () => {
                // Allow a later attempt to look the organisation up again.
                if (this.currentOrganisation === organisationId) {
                    this.currentOrganisation = undefined;
                }
                done();
            }
        });
    }

    private clearTheme() {
        const scripts = this._document.querySelectorAll('script[data-iapply-theme]');
        scripts.forEach((script: Element) => script.remove());

        const styles = this._document.querySelectorAll('link[data-iapply-theme]');
        styles.forEach((style: Element) => style.remove());

        const favicon: HTMLLinkElement | null = this._document.querySelector('#appIcon');
        if (favicon) {
            let href = favicon.getAttribute('data-href') || '';
            this.renderer.setAttribute(favicon, 'href', href);
            // $('#favicon').attr('href', $('#favicon').attr('data-href'));
        }

        this.themeName = undefined;
        this.currentTheme = undefined;
        this.currentOrganisation = undefined;
        this.themeLoaded.set(false);
        this.resetPending();
    }
}
